import React, { useRef, useState } from 'react';
import { styled } from '@withjoy/joykit';
import { INavTheme } from './Workspace';
import { WorkspaceGridItem } from './WorkspaceGridItem';
import { EventConfiguration } from '@apps/workspace/Workspace';

export interface IDashboardComponent {
  instanceId: string;
  component?: React.ReactNode;
  colSpan?: number;
  rowSpan?: number;
  allowScroll?: boolean;
  color?: {
    start: string;
    end: string;
  };
}

interface WorkspaceGridProps {
  layout: IDashboardComponent[];
  theme?: INavTheme;
  eventHandle?: string;
  eventConfiguration?: EventConfiguration;
}

const WorkspaceGridContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  grid-gap: 1rem;
`;

export const WorkspaceGrid: React.FC<WorkspaceGridProps> = ({ layout, theme, eventHandle, eventConfiguration }) => {
  const ref = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [orderedLayout, setOrderedLayout] = useState<IDashboardComponent[]>([...layout]);
  const dragStartIndex = useRef<number | null>(null);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    /*
    const data = event.dataTransfer.getData('text/plain');
   
    const droppedComponent = JSON.parse(data) as IDashboardComponent;
    if (layout) {
      layout.push(droppedComponent);
    } else {
      layout = [droppedComponent];
    }
      */

    const gridContainer = gridContainerRef.current;
    if (gridContainer) {
      const gridRect = gridContainer.getBoundingClientRect();
      const gridComputedStyle = window.getComputedStyle(gridContainer);
      const columnCount = gridComputedStyle.getPropertyValue('grid-template-columns').split(' ').length;
      const rowCount = gridComputedStyle.getPropertyValue('grid-template-rows').split(' ').length;

      const cellWidth = gridRect.width / columnCount;
      const cellHeight = gridRect.height / rowCount;

      const dropX = event.clientX - gridRect.left;
      const dropY = event.clientY - gridRect.top;

      const droppedColumn = Math.floor(dropX / cellWidth);
      const droppedRow = Math.floor(dropY / cellHeight);
      const linearIndex = droppedRow * columnCount + droppedColumn;
      if (dragStartIndex.current !== null && dragStartIndex.current >= 0 && linearIndex >= 0 && dragStartIndex.current !== linearIndex && linearIndex < orderedLayout.length) {
        const updated = [...orderedLayout];
        const [removed] = updated.splice(dragStartIndex.current!, 1);
        updated.splice(linearIndex, 0, removed);

        setOrderedLayout(updated);
      }
      event.currentTarget.style.boxShadow = `0px 1px 3px ${theme?.shadow ?? 'rgba(0,0,0,0.5)'}`;
      dragStartIndex.current = null;
    }
  };
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    //event.currentTarget.style.border = `2px solid ${theme?.accent ?? 'blue'}`;
    event.currentTarget.style.boxShadow = `0px 1px 1px ${theme?.shadow ?? 'rgba(0,0,0,0.5)'}`;
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.style.boxShadow = `0px 1px 3px ${theme?.shadow ?? 'rgba(0,0,0,0.5)'}`;
  };

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    event.dataTransfer.setData('text', (event.target as HTMLDivElement).id);
    const gridContainer = gridContainerRef.current;
    if (gridContainer) {
      const gridRect = gridContainer.getBoundingClientRect();
      const gridComputedStyle = window.getComputedStyle(gridContainer);
      const columnCount = gridComputedStyle.getPropertyValue('grid-template-columns').split(' ').length;
      const rowCount = gridComputedStyle.getPropertyValue('grid-template-rows').split(' ').length;

      const cellWidth = gridRect.width / columnCount;
      const cellHeight = gridRect.height / rowCount;

      const dropX = event.clientX - gridRect.left;
      const dropY = event.clientY - gridRect.top;

      const droppedColumn = Math.floor(dropX / cellWidth);
      const droppedRow = Math.floor(dropY / cellHeight);
      const linearIndex = droppedRow * columnCount + droppedColumn;
      dragStartIndex.current = linearIndex;
    }
  };
  return (
    <div
      ref={ref}
      style={{ position: 'relative', width: '100%', maxWidth: '1190px', margin: '0 auto' }}
    >
      <WorkspaceGridContainer ref={gridContainerRef}>
        {orderedLayout?.map((item, index) => {
          const Component = item.component as React.FC<{ eventHandle?: string; eventConfiguration?: EventConfiguration }>;
          return (
            <WorkspaceGridItem
              handleDragStart={handleDragStart}
              handleDrop={handleDrop}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              id={item.instanceId}
              allowScroll={item.allowScroll}
              highlightColor={item.color?.start ?? 'white'}
              shadedColor={item.color?.end ?? 'white'}
              shadowColor={'rgba(0,0,0,0.5)'}
              key={item.instanceId}
            >
              {item.component ? (
                <Component
                  eventHandle={eventHandle}
                  eventConfiguration={eventConfiguration}
                />
              ) : (
                `${item.instanceId} hi! ${index + 1}`
              )}
            </WorkspaceGridItem>
          );
        })}
      </WorkspaceGridContainer>
    </div>
  );
};
